import React, { memo, useCallback, useId, useMemo, useState } from 'react';
import {
  StyleSheet,
  View,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewProps,
  type ViewStyle,
} from 'react-native';
import Svg, { Defs, LinearGradient as SvgLinearGradient, Rect, Stop } from 'react-native-svg';

import { useRtl } from '../../../shared/i18n/rtl';
import { normalizeSvgStopColor } from '../gradientSvgColor';
import { normalizeGradientLocations, resolveGradientVector, type GradientPoint } from './gradientVector';

export type AppLinearGradientProps = {
  colors: readonly string[];
  locations?: readonly number[];
  start?: GradientPoint;
  end?: GradientPoint;
  style?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
  pointerEvents?: ViewProps['pointerEvents'];
  /** Mirror horizontal vector when RTL (default true). */
  respectRtl?: boolean;
};

const DEFAULT_START: GradientPoint = { x: 0.5, y: 0 };
const DEFAULT_END: GradientPoint = { x: 0.5, y: 1 };

function GradientLayer({
  grad_id,
  width,
  height,
  colors,
  locations,
  start,
  end,
}: {
  grad_id: string;
  width: number;
  height: number;
  colors: readonly string[];
  locations: number[];
  start: GradientPoint;
  end: GradientPoint;
}) {
  if (width <= 0 || height <= 0 || colors.length === 0) {
    return null;
  }

  return (
    <Svg width={width} height={height} style={styles.svg_layer} pointerEvents="none">
      <Defs>
        <SvgLinearGradient
          id={grad_id}
          x1={String(start.x)}
          y1={String(start.y)}
          x2={String(end.x)}
          y2={String(end.y)}
          gradientUnits="objectBoundingBox">
          {colors.map((color, index) => (
            <Stop
              key={`${grad_id}-stop-${index}`}
              offset={String(locations[index] ?? 0)}
              stopColor={normalizeSvgStopColor(color)}
            />
          ))}
        </SvgLinearGradient>
      </Defs>
      <Rect x={0} y={0} width={width} height={height} fill={`url(#${grad_id})`} />
    </Svg>
  );
}

/** SVG gradient replacement for `expo-linear-gradient` — v1 / v2 / v3 shared. */
export const AppLinearGradient = memo(function AppLinearGradient({
  colors,
  locations,
  start = DEFAULT_START,
  end = DEFAULT_END,
  style,
  children,
  pointerEvents = 'auto',
  respectRtl = true,
}: AppLinearGradientProps) {
  const react_id = useId();
  const grad_id = useMemo(() => `app-grad-${react_id.replace(/:/g, '')}`, [react_id]);
  const is_rtl = useRtl().isRTL;
  const [layout, set_layout] = useState({ width: 0, height: 0 });

  const resolved_vector = useMemo(
    () => resolveGradientVector(start, end, is_rtl, respectRtl),
    [end, is_rtl, respectRtl, start],
  );
  const normalized_locations = useMemo(
    () => normalizeGradientLocations(colors.length, locations),
    [colors.length, locations],
  );

  const on_layout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    set_layout(prev =>
      prev.width === width && prev.height === height ? prev : { width, height },
    );
  }, []);

  return (
    <View style={[styles.root, style]} pointerEvents={pointerEvents} onLayout={on_layout}>
      <GradientLayer
        grad_id={grad_id}
        width={layout.width}
        height={layout.height}
        colors={colors}
        locations={normalized_locations}
        start={resolved_vector.start}
        end={resolved_vector.end}
      />
      {children}
    </View>
  );
});

const styles = StyleSheet.create({
  root: {
    overflow: 'hidden',
  },
  svg_layer: {
    ...StyleSheet.absoluteFillObject,
  },
});
